import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import DateRange from "../../components/calendar/DateRangePicker";
import { useNavigate } from "react-router-dom";
import { PERMISSIONS } from "../../contexts/permissions";
import { PermissionGate } from "../../helpers/PermissionGate";
import {
  fetchAllReportTypes,
  exportReportAsCSV,
} from "../../sdk/reportsAndanalytics/reportsAndAnalytics";
import ExportReportCSV from "../../components/export/ExportReportCSV";
import useAudit from "../../hooks/useAudit";

const Reports = () => {
  const [reportTypesData, setReportTypesData] = useState({});
  const [reportType, setReportType] = useState("");
  const [reportOptions, setReportOptions] = useState([]);
  const [report, setReport] = useState(null);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [customerFocusedInput, setCustomerFocusedInput] = useState(null);

  const navigate = useNavigate();
  const { logAudit } = useAudit();

  useEffect(() => {
    const loadReportTypes = async () => {
      try {
        const data = await fetchAllReportTypes();
        setReportTypesData(data || {});
      } catch (error) {
        console.error("Failed to load report types", error);
      }
    };

    loadReportTypes();
  }, []);

  const handleReportTypeChange = (e) => {
    const selectedType = e.target.value;
    setReportType(selectedType);
    setReport("");

    if (selectedType && selectedType !== "0") {
      setReportOptions(reportTypesData[selectedType] || []);
    } else {
      setReportOptions([]);
    }
  };

  const handleReportChange = (e) => {
    const selectedKey = e.target.value;
    setReport(selectedKey);
  };

  const navigateToTabReports = () => {
    if (!reportType || reportType === "0" || !report || report === "0") {
      alert("Please select both report type and report before generating.");
      return;
    }

    const selectedReport = reportOptions?.find((r) => r?.key === report);

    const fromDateString = fromDate ? fromDate.toISOString() : null;
    const toDateString = toDate ? toDate.toISOString() : null;

    sessionStorage.setItem(
      "tabularReportContext",
      JSON.stringify({
        reportKey: report,
        reportLabel: selectedReport?.label,
        fromDate: fromDateString,
        toDate: toDateString,
      }),
    );

    logAudit({
      category: "Reports",
      actionCode: "CONFIGURE_REPORT_FILTERS",
      description: `Configured report filters for (${selectedReport?.label})`,
      page: "Reports",
      tab: "Reports Dashboard",
      section: "Filters",
      metadata: {
        reportName: selectedReport?.label,
        reportType,
        fromDate,
        toDate,
      },
    });

    navigate(`/dashboard/reports/${encodeURIComponent(reportType)}/${report}`, {
      state: {
        reportKey: report,
        reportLabel: selectedReport?.label,
        fromDate: fromDateString,
        toDate: toDateString,
      },
    });

    logAudit({
      category: "Reports",
      actionCode: "EXPORT_REPORT_CSV",
      description: `Exported report (${selectedReport?.label}) as CSV`,
      page: "Reports",
      tab: "Generated Report",
      section: "Reports Export",
      metadata: {
        reportName: selectedReport?.label,
        format: "CSV",
      },
    });
  };

  useEffect(() => {
    logAudit({
      category: "Reports",
      actionCode: "VIEW_REPORTS",
      description: "Viewed reports page",
      page: "Reports",
      tab: "Reports Dashboard",
      section: "Reports",
      metadata: {
        route: "/dashboard/reports",
      },
    });
  }, []);

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Anansi || Reports</title>
      </Helmet>
      <div className="w-[100%] bg-[#FFF] h-full overflow-y-auto">
        <div className="bg-[#FFF] mx-[20px]">
          <div className="flex items-center gap-2">
            <div className="flex items-center text-[#074073] text-[20px] font-normal">
              Reports
            </div>
            <svg
              width="12"
              height="24"
              viewBox="0 0 12 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M2.45199 6.58023L3.51299 5.52024L9.29199 11.2972C9.38514 11.3898 9.45907 11.4999 9.50952 11.6211C9.55997 11.7424 9.58594 11.8724 9.58594 12.0037C9.58594 12.1351 9.55997 12.2651 9.50952 12.3863C9.45907 12.5076 9.38514 12.6177 9.29199 12.7102L3.51299 18.4902L2.45299 17.4302L7.87699 12.0052L2.45199 6.58023Z"
                fill="#074073"
              />
            </svg>
          </div>

          <div className="flex-col flex justify-center items-center w-full mt-12">
            <div className="flex-col justify-center items-center w-[702px] px-5">
              <div className="text-[#000000] text-[14px] font-normal">
                Generate detailed reports based on your preferred criteria.
                Select a date range and filters to <br /> customize the report.
              </div>
              <div className="text-[#000000] text-[16px] font-normal mt-5">
                Report Type
              </div>
              <div className="mt-2 flex items-center gap-[5px] relative w-[659px]">
                <select
                  className="w-[659px] h-[51px] px-[10px] bg-[#EAEAEA] rounded-md appearance-none focus:outline-none"
                  value={reportType}
                  onChange={handleReportTypeChange}
                >
                  <option value="0">-- Select Report Type</option>
                  {Object.keys(reportTypesData)?.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
                <span className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                  <svg
                    width="14"
                    height="7"
                    viewBox="0 0 14 7"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M13 1L7 6L1 1"
                      stroke="#898989"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </span>
              </div>

              <div className="text-[#000000] text-[16px] font-normal mt-5">
                Report
              </div>
              <div className="mt-2 flex items-center gap-[5px] relative w-[659px]">
                <select
                  name="report"
                  id="report"
                  className="w-[659px] h-[51px] px-[10px] bg-[#EAEAEA] rounded-md appearance-none focus:outline-none"
                  onChange={handleReportChange}
                  value={report}
                >
                  <option value="0">-- Select Report</option>
                  {reportOptions?.map((r) => (
                    <option key={r?.key} value={r?.key}>
                      {r?.label}
                    </option>
                  ))}
                </select>

                <span className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                  <svg
                    width="14"
                    height="7"
                    viewBox="0 0 14 7"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M13 1L7 6L1 1"
                      stroke="#898989"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </span>
              </div>

              <div className="text-[#000000] text-[16px] font-normal mt-5">
                Date Range
              </div>

              <div className="mt-2 flex items-center gap-[5px] relative">
                <div className="w-[659px] h-[51px] bg-[#EAEAEA] rounded-md flex items-center">
                  <DateRange
                    startDate={fromDate}
                    endDate={toDate}
                    setStartDate={setFromDate}
                    setEndDate={setToDate}
                    focusedInput={customerFocusedInput}
                    setFocusedInput={setCustomerFocusedInput}
                    className="w-full h-full bg-[#EAEAEA] focus:outline-none"
                  />
                </div>
              </div>

              <PermissionGate required={[PERMISSIONS?.RP?.EXPORT]}>
                <div className="flex w-[100%] items-center justify-between mt-8 mb-5">
                  <ExportReportCSV
                    reportType={reportType}
                    reportKey={report}
                    startDate={fromDate}
                    endDate={toDate}
                    limit={1000000}
                    logAudit={logAudit}
                    exportReportAsCSV={exportReportAsCSV}
                  />

                  <button
                    onClick={navigateToTabReports}
                    type="button"
                    disabled={
                      !reportType ||
                      reportType === "0" ||
                      !report ||
                      report === "0"
                    }
                    className={`text-white bg-[#074073] hover:bg-[#074073] focus:outline-none font-medium rounded-[5px] text-sm px-8 py-2.5 text-center w-[322px] ${
                      !reportType ||
                      reportType === "0" ||
                      !report ||
                      report === "0"
                        ? "opacity-50 cursor-not-allowed"
                        : "cursor-pointer"
                    }`}
                  >
                    Generate Report
                  </button>
                </div>
              </PermissionGate>
            </div>
            <div className="flex justify-center items-center border border-[#92C4BF] rounded-[10px] width-[702px] mt-5">
              <div
                className="px-3 py-2 text-[13px] font-poppins font-normal italic"
                style={{
                  lineHeight: "100%",
                  letterSpacing: "0%",
                }}
              >
                Report generation may take a little longer depending on the data
                volume. Please wait while we process <br /> your request. You
                will be notified once the report is ready.
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Reports;
